import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { SubscriptionTier } from "@hyreese/constants";
import { authApi } from "@/api/auth";
import { tokenStore } from "@/lib/token-store";
import type {
  AuthResponse,
  JwtPayload,
  LoginRequest,
  RegisterRequest,
  UserProfile,
} from "@/types/api/auth.types";

const ONBOARDING_SKIP_KEY = "hyreese_onboarding_skip";

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  onboardingCompleted: boolean;
  onboardingSkipped: boolean;
}

export interface AuthContextValue extends AuthState {
  tier: SubscriptionTier | null;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

function handleAuthResponse(response: AuthResponse): UserProfile | null {
  tokenStore.setAccessToken(response.access_token);
  tokenStore.setRefreshToken(response.refresh_token);
  return response.user ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    let skipped = false;
    try {
      skipped = localStorage.getItem(ONBOARDING_SKIP_KEY) === "true";
    } catch {
      /* ignore */
    }
    return {
      user: null,
      isAuthenticated: false,
      isLoading: true,
      onboardingCompleted: true,
      onboardingSkipped: skipped,
    };
  });

  const tier = useMemo(
    () => (state.user?.subscription_tier as SubscriptionTier) ?? null,
    [state.user?.subscription_tier],
  );

  const setAuthenticated = useCallback(
    (user: UserProfile, onboarded?: boolean) => {
      setState((prev) => ({
        user,
        isAuthenticated: true,
        isLoading: false,
        onboardingCompleted: onboarded ?? user.onboarding_completed ?? true,
        onboardingSkipped: prev.onboardingSkipped,
      }));
    },
    [],
  );

  const setUnauthenticated = useCallback(() => {
    tokenStore.clear();
    try {
      localStorage.removeItem(ONBOARDING_SKIP_KEY);
    } catch {
      /* ignore */
    }
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      onboardingCompleted: true,
      onboardingSkipped: false,
    });
  }, []);

  const completeOnboarding = useCallback(() => {
    try {
      localStorage.removeItem(ONBOARDING_SKIP_KEY);
    } catch {
      /* ignore */
    }
    setState((prev) => ({
      ...prev,
      onboardingCompleted: true,
      onboardingSkipped: false,
    }));
  }, []);

  const skipOnboarding = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDING_SKIP_KEY, "true");
    } catch {
      /* ignore */
    }
    setState((prev) => ({ ...prev, onboardingSkipped: true }));
  }, []);

  // Attempt to restore session on mount
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const accessToken = tokenStore.getAccessToken();
      const refreshTokenValue = tokenStore.getRefreshToken();

      // No tokens at all — user hasn't logged in
      if (!accessToken && !refreshTokenValue) {
        if (!cancelled) setUnauthenticated();
        return;
      }

      try {
        // Refresh if: no access token (page reload) or access token expired
        const needsRefresh =
          !accessToken || (accessToken && isTokenExpired(accessToken));

        if (needsRefresh && refreshTokenValue) {
          const tokens = await authApi.refresh(refreshTokenValue);
          tokenStore.setAccessToken(tokens.access_token);
          tokenStore.setRefreshToken(tokens.refresh_token);
        }

        // Fetch the user profile with the (potentially new) access token
        const user = await authApi.me();
        if (!cancelled) setAuthenticated(user);
      } catch {
        if (!cancelled) setUnauthenticated();
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [setAuthenticated, setUnauthenticated]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const response = await authApi.login(credentials);
      const user = handleAuthResponse(response);
      if (user) {
        setAuthenticated(user);
      } else {
        // Login response should include user, but fetch as fallback
        const profile = await authApi.me();
        setAuthenticated(profile);
      }
    },
    [setAuthenticated],
  );

  const register = useCallback(
    async (data: RegisterRequest) => {
      const response = await authApi.register(data);
      const user = handleAuthResponse(response);
      if (user) {
        setAuthenticated(user, false);
      } else {
        const profile = await authApi.me();
        setAuthenticated(profile, false);
      }
    },
    [setAuthenticated],
  );

  const googleLogin = useCallback(
    async (credential: string) => {
      const response = await authApi.googleLogin({ credential });
      const user = handleAuthResponse(response);
      if (user) {
        // New Google users need onboarding; existing users skip it
        const isNewUser = response.user?.onboarding_completed === false;
        setAuthenticated(user, isNewUser ? false : undefined);
      } else {
        const profile = await authApi.me();
        setAuthenticated(profile);
      }
    },
    [setAuthenticated],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Logout API failing shouldn't block local cleanup
    }
    setUnauthenticated();
  }, [setUnauthenticated]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      tier,
      login,
      register,
      googleLogin,
      logout,
      completeOnboarding,
      skipOnboarding,
    }),
    [
      state,
      tier,
      login,
      register,
      googleLogin,
      logout,
      completeOnboarding,
      skipOnboarding,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
